import { useState, useMemo, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Search, CheckCircle, XCircle, ArrowUpRight, ArrowDownLeft, MoreVertical, Sparkles, ChevronLeft, ChevronRight, AlertCircle, Brain, Wand2, FileText } from 'lucide-react'
import { toast } from 'sonner'
import { reconciliationApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { BankTransaction, TransactionStatus, ApproveTransactionRequest } from '@/types'
import { usePermissions } from '@/contexts/PermissionContext'
import { format } from 'date-fns'
import { cn } from '@/lib/utils'
import { useDebounce } from '@/hooks/useDebounce'
import { ConfidenceBadge, AIMetricsDashboard, CategorySuggestionBadge } from '@/components/reconciliation'

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

const STATUS_CONFIG: Record<TransactionStatus, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  PENDING: { label: 'Pending', variant: 'outline' },
  MATCHED: { label: 'Matched', variant: 'default' },
  EXPENSE_CREATED: { label: 'Expense Created', variant: 'default' },
  INCOME_RECORDED: { label: 'Income Recorded', variant: 'default' },
  TRANSFER: { label: 'Transfer', variant: 'secondary' },
  IGNORED: { label: 'Ignored', variant: 'secondary' },
  RECONCILED: { label: 'Reconciled', variant: 'default' },
  AUTO_APPROVED: { label: 'Auto-Approved', variant: 'default' },
  EXCLUDED: { label: 'Excluded', variant: 'secondary' },
}

const ALL_STATUSES = '__all__'

export default function TransactionInboxPage() {
  const { canAddEdit } = usePermissions()
  const canEdit = canAddEdit('RECONCILIATION')
  const [searchTerm, setSearchTerm] = useState('')
  const [accountFilter, setAccountFilter] = useState<string>(ALL_STATUSES)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [activeTab, setActiveTab] = useState('pending')
  const [pageSize, setPageSize] = useState(25)
  const [currentPage, setCurrentPage] = useState(1)

  // Approval dialog state
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false)
  const [transactionToApprove, setTransactionToApprove] = useState<BankTransaction | null>(null)
  const [createRuleOnApprove, setCreateRuleOnApprove] = useState(false)
  const [ruleName, setRuleName] = useState('')

  const queryClient = useQueryClient()

  // Debounce search term for server-side search
  const debouncedSearch = useDebounce(searchTerm, 300)

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearch])

  // Map activeTab to API status filter
  const getStatusForTab = (tab: string): string | undefined => {
    switch (tab) {
      case 'pending':
        return 'PENDING'
      case 'matched':
        // Use comma-separated statuses for server-side filtering
        return 'MATCHED,EXPENSE_CREATED,INCOME_RECORDED,AUTO_APPROVED'
      case 'ignored':
        return 'IGNORED'
      default:
        return undefined
    }
  }

  const statusFilter = getStatusForTab(activeTab)

  const { data: transactionsData, isLoading } = useQuery({
    queryKey: ['transactions', activeTab, accountFilter, currentPage, pageSize, debouncedSearch],
    queryFn: () => reconciliationApi.transactions.list({
      status: statusFilter,
      bank_account_id: accountFilter !== ALL_STATUSES ? accountFilter : undefined,
      page: currentPage,
      page_size: pageSize,
      search: debouncedSearch || undefined,
    }),
  })

  const { data: accountsData } = useQuery({
    queryKey: ['bank-accounts'],
    queryFn: () => reconciliationApi.bankAccounts.list(),
  })

  const { data: dashboardData } = useQuery({
    queryKey: ['reconciliation-dashboard'],
    queryFn: () => reconciliationApi.dashboard.get(),
  })

  // Review queue data for "Need Review" tab
  const { data: reviewQueueData, isLoading: isLoadingReviewQueue } = useQuery({
    queryKey: ['review-queue', accountFilter, currentPage, pageSize],
    queryFn: () => reconciliationApi.reviewQueue.list({
      bank_account_id: accountFilter !== ALL_STATUSES ? accountFilter : undefined,
      page: currentPage,
      page_size: pageSize,
    }),
    enabled: activeTab === 'need-review',
  })

  // Review queue count for badge
  const { data: reviewQueueCount } = useQuery({
    queryKey: ['review-queue-count'],
    queryFn: () => reconciliationApi.reviewQueue.getCount(),
  })

  // Approve transaction from review queue
  const approveReviewMutation = useMutation({
    mutationFn: (data: ApproveTransactionRequest & { id: string }) =>
      reconciliationApi.reviewQueue.approve(data.id, {
        category_id: data.category_id,
        vendor: data.vendor,
        create_rule: data.create_rule,
        rule_name: data.rule_name,
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['review-queue'] })
      queryClient.invalidateQueries({ queryKey: ['review-queue-count'] })
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
      queryClient.invalidateQueries({ queryKey: ['reconciliation-dashboard'] })
      if (variables.create_rule) {
        queryClient.invalidateQueries({ queryKey: ['reconciliation-rules'] })
        toast.success('Transaction approved and rule created!')
      } else {
        toast.success('Transaction approved')
      }
      setApprovalDialogOpen(false)
      setTransactionToApprove(null)
      setCreateRuleOnApprove(false)
      setRuleName('')
    },
    onError: () => toast.error('Failed to approve transaction'),
  })

  // Reject transaction from review queue
  const rejectReviewMutation = useMutation({
    mutationFn: (id: string) => reconciliationApi.reviewQueue.reject(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['review-queue'] })
      queryClient.invalidateQueries({ queryKey: ['review-queue-count'] })
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
      queryClient.invalidateQueries({ queryKey: ['reconciliation-dashboard'] })
      toast.success('Transaction rejected')
    },
    onError: () => toast.error('Failed to reject transaction'),
  })

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: TransactionStatus }) =>
      reconciliationApi.transactions.updateStatus(id, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
      queryClient.invalidateQueries({ queryKey: ['reconciliation-dashboard'] })
      toast.success('Transaction updated')
    },
    onError: () => toast.error('Failed to update transaction'),
  })

  const bulkUpdateMutation = useMutation({
    mutationFn: ({ ids, status }: { ids: string[]; status: TransactionStatus }) =>
      reconciliationApi.transactions.bulkUpdateStatus({ transaction_ids: ids, status }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
      queryClient.invalidateQueries({ queryKey: ['reconciliation-dashboard'] })
      toast.success(`Updated ${result.updated_count} transactions`)
      setSelectedIds(new Set())
    },
    onError: () => toast.error('Failed to update transactions'),
  })

  // API returns data with pagination info - handle both array and object responses
  const transactions = useMemo(() => {
    // For review queue tab, use review queue data
    if (activeTab === 'need-review') {
      return reviewQueueData?.transactions || []
    }
    if (Array.isArray(transactionsData)) return transactionsData
    return transactionsData?.transactions || []
  }, [transactionsData, reviewQueueData, activeTab])

  // Server-side filtering is now used for all tabs, no client-side filtering needed
  const filteredTransactions = transactions

  // Server-side pagination data
  const paginationData = useMemo(() => {
    // For review queue tab, use review queue pagination
    if (activeTab === 'need-review') {
      return {
        total: reviewQueueData?.total || 0,
        totalPages: reviewQueueData?.total_pages || 1,
      }
    }
    if (Array.isArray(transactionsData)) {
      // Fallback for array response
      return {
        total: transactions.length,
        totalPages: Math.ceil(transactions.length / pageSize),
      }
    }
    return {
      total: transactionsData?.total || 0,
      totalPages: transactionsData?.total_pages || 1,
    }
  }, [transactionsData, reviewQueueData, transactions.length, pageSize, activeTab])

  const totalRecords = paginationData.total
  const totalPages = paginationData.totalPages

  // Determine loading state based on active tab
  const isLoadingData = activeTab === 'need-review' ? isLoadingReviewQueue : isLoading

  // Use server-paginated transactions directly (no client-side slicing needed)
  const paginatedTransactions = filteredTransactions

  const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1
  const endRecord = Math.min(currentPage * pageSize, totalRecords)

  const handlePageSizeChange = (newSize: string) => {
    setPageSize(Number(newSize))
    setCurrentPage(1)
  }

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1)
    }
  }

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
    }
  }

  // Reset to page 1 when filters change
  const handleTabChange = (tab: string) => {
    setActiveTab(tab)
    setCurrentPage(1)
    setSelectedIds(new Set())
  }

  const toggleSelect = (id: string) => {
    const newSet = new Set(selectedIds)
    if (newSet.has(id)) newSet.delete(id)
    else newSet.add(id)
    setSelectedIds(newSet)
  }

  const toggleSelectAll = () => {
    if (selectedIds.size === paginatedTransactions.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(paginatedTransactions.map(t => t.transaction_id)))
    }
  }

  const handleBulkAction = (status: TransactionStatus) => {
    if (selectedIds.size === 0) return
    bulkUpdateMutation.mutate({ ids: Array.from(selectedIds), status })
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Transaction Inbox</h1>
          <p className="text-muted-foreground">Review and reconcile bank transactions</p>
        </div>
      </div>

      {/* Dashboard Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard title="Pending" value={dashboardData?.pending_transactions || 0} variant="warning" />
        <StatCard
          title="Need Review"
          value={reviewQueueCount?.count || 0}
          variant="danger"
          icon={<AlertCircle className="h-4 w-4" />}
        />
        <StatCard title="Matched" value={dashboardData?.matched_transactions || 0} variant="success" />
        <StatCard title="Reconciled" value={dashboardData?.reconciled_transactions || 0} variant="default" />
        <StatCard title="Auto-Processed" value={`${(dashboardData?.auto_processed_percent || 0).toFixed(0)}%`} variant="info" />
      </div>

      {/* AI Metrics Dashboard - Compact view */}
      <AIMetricsDashboard compact />

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full md:w-auto">
              <TabsList>
                <TabsTrigger value="pending">Pending</TabsTrigger>
                <TabsTrigger value="need-review" className="relative">
                  <Brain className="h-4 w-4 mr-1" />
                  Need Review
                  {(reviewQueueCount?.count || 0) > 0 && (
                    <span className="ml-1.5 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-red-100 bg-red-600 rounded-full">
                      {reviewQueueCount?.count}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger value="matched">Matched</TabsTrigger>
                <TabsTrigger value="ignored">Ignored</TabsTrigger>
                <TabsTrigger value="all">All</TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="flex-1 flex items-center gap-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search transactions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={accountFilter} onValueChange={(v) => { setAccountFilter(v); setCurrentPage(1) }}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Accounts" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_STATUSES}>All Accounts</SelectItem>
                  {(Array.isArray(accountsData) ? accountsData : accountsData?.bank_accounts || []).map((account) => (
                    <SelectItem key={account.bank_account_id} value={account.bank_account_id}>
                      {account.account_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Bulk Actions - not shown for review queue */}
          {selectedIds.size > 0 && canEdit && activeTab !== 'need-review' && (
            <div className="flex items-center gap-2 mb-4 p-2 bg-muted rounded-lg">
              <span className="text-sm font-medium">{selectedIds.size} selected</span>
              <Button size="sm" variant="outline" onClick={() => handleBulkAction('RECONCILED')}>
                <CheckCircle className="h-4 w-4 mr-1" /> Reconcile
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleBulkAction('IGNORED')}>
                <XCircle className="h-4 w-4 mr-1" /> Ignore
              </Button>
            </div>
          )}

          <div className="h-[calc(100vh-450px)] overflow-auto">
            <TransactionTable
              transactions={paginatedTransactions}
              isLoading={isLoadingData}
              selectedIds={selectedIds}
              onToggleSelect={toggleSelect}
              onToggleSelectAll={toggleSelectAll}
              onUpdateStatus={(id, status) => updateStatusMutation.mutate({ id, status })}
              onApproveReview={(transaction) => {
                setTransactionToApprove(transaction)
                // Generate a default rule name from the description
                const defaultRuleName = transaction.suggested_vendor ||
                  transaction.description.split(' ').slice(0, 3).join(' ')
                setRuleName(defaultRuleName)
                setApprovalDialogOpen(true)
              }}
              onRejectReview={(id) => rejectReviewMutation.mutate(id)}
              canAddEdit={canEdit}
              isReviewQueue={activeTab === 'need-review'}
            />
          </div>
          {/* Pagination Controls */}
          <div className="flex items-center gap-3 px-4 py-2 border-t mt-2">
            <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
              <SelectTrigger className="w-[70px] h-7 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-xs text-muted-foreground">
              {startRecord}-{endRecord} of {totalRecords}
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={goToNextPage}
                disabled={currentPage >= totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Approval Dialog with Create Rule Option */}
      <Dialog open={approvalDialogOpen} onOpenChange={setApprovalDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Approve Transaction
            </DialogTitle>
            <DialogDescription>
              Approve this transaction and optionally create a rule for similar transactions.
            </DialogDescription>
          </DialogHeader>

          {transactionToApprove && (
            <div className="space-y-4">
              {/* Transaction Details */}
              <div className="rounded-lg border p-3 bg-muted/50">
                <div className="space-y-3 text-sm">
                  {/* Original Description */}
                  <div>
                    <span className="text-muted-foreground">Original Description:</span>
                    <p className="font-medium text-xs bg-muted p-2 rounded mt-1 break-all">
                      {transactionToApprove.raw_description || transactionToApprove.description}
                    </p>
                  </div>
                  {/* AI Description */}
                  {(transactionToApprove.normalized_description || transactionToApprove.suggested_vendor) && (
                    <div>
                      <span className="text-muted-foreground flex items-center gap-1">
                        <Sparkles className="h-3 w-3 text-purple-600" /> AI Description:
                      </span>
                      <p className="font-medium text-purple-700 dark:text-purple-400">
                        {transactionToApprove.normalized_description || transactionToApprove.suggested_vendor}
                      </p>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <span className="text-muted-foreground">Amount:</span>
                      <p className={cn(
                        'font-medium',
                        transactionToApprove.direction === 'CREDIT' ? 'text-green-600' : 'text-red-600'
                      )}>
                        {transactionToApprove.direction === 'CREDIT' ? '+' : '-'}
                        {transactionToApprove.currency} {Math.abs(transactionToApprove.amount).toLocaleString()}
                      </p>
                    </div>
                    {transactionToApprove.suggested_category_id && (
                      <div>
                        <span className="text-muted-foreground">Suggested Category:</span>
                        <p className="font-medium">Category ID: {transactionToApprove.suggested_category_id}</p>
                      </div>
                    )}
                  </div>
                </div>
                {transactionToApprove.ai_reasoning && (
                  <div className="mt-2 pt-2 border-t">
                    <span className="text-muted-foreground text-sm flex items-center gap-1">
                      <Sparkles className="h-3 w-3" /> AI Reasoning:
                    </span>
                    <p className="text-sm italic">{transactionToApprove.ai_reasoning}</p>
                  </div>
                )}
              </div>

              {/* Create Rule Option */}
              <div className="rounded-lg border p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="create-rule"
                    checked={createRuleOnApprove}
                    onCheckedChange={(checked) => setCreateRuleOnApprove(checked === true)}
                  />
                  <div className="space-y-1">
                    <Label htmlFor="create-rule" className="flex items-center gap-2 cursor-pointer">
                      <Wand2 className="h-4 w-4 text-purple-600" />
                      Create a rule for similar transactions
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Automatically categorize future transactions with similar descriptions.
                    </p>
                  </div>
                </div>

                {createRuleOnApprove && (
                  <div className="ml-6 space-y-2">
                    <Label htmlFor="rule-name">Rule Name</Label>
                    <Input
                      id="rule-name"
                      value={ruleName}
                      onChange={(e) => setRuleName(e.target.value)}
                      placeholder="e.g., AWS Monthly Subscription"
                    />
                    <p className="text-xs text-muted-foreground">
                      The rule will match transactions containing "{transactionToApprove.suggested_vendor || transactionToApprove.description.split(' ')[0]}"
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setApprovalDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (transactionToApprove) {
                  approveReviewMutation.mutate({
                    id: transactionToApprove.transaction_id,
                    category_id: transactionToApprove.suggested_category_id,
                    vendor: transactionToApprove.suggested_vendor,
                    create_rule: createRuleOnApprove,
                    rule_name: createRuleOnApprove ? ruleName : undefined,
                  })
                }
              }}
              disabled={approveReviewMutation.isPending || (createRuleOnApprove && !ruleName.trim())}
            >
              {approveReviewMutation.isPending ? 'Approving...' : 'Approve'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

interface StatCardProps {
  title: string
  value: number | string
  variant: 'default' | 'success' | 'warning' | 'info' | 'danger'
  icon?: React.ReactNode
}

function StatCard({ title, value, variant, icon }: StatCardProps) {
  const colors = {
    default: 'bg-card',
    success: 'bg-green-50 dark:bg-green-950',
    warning: 'bg-yellow-50 dark:bg-yellow-950',
    info: 'bg-blue-50 dark:bg-blue-950',
    danger: 'bg-red-50 dark:bg-red-950',
  }
  return (
    <Card className={cn(colors[variant])}>
      <CardContent className="pt-4">
        <div className="flex items-center gap-1.5">
          {icon}
          <p className="text-sm text-muted-foreground">{title}</p>
        </div>
        <p className="text-2xl font-bold">{value}</p>
      </CardContent>
    </Card>
  )
}

// Processing Method Badge Component
function ProcessingMethodBadge({ method }: { method?: string }) {
  if (!method) {
    return <span className="text-xs text-muted-foreground">-</span>
  }

  const config: Record<string, { label: string; icon: React.ReactNode; className: string }> = {
    AI: {
      label: 'AI',
      icon: <Sparkles className="h-3 w-3" />,
      className: 'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-800',
    },
    RULE: {
      label: 'Rule',
      icon: <FileText className="h-3 w-3" />,
      className: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800',
    },
    MANUAL: {
      label: 'Manual',
      icon: <CheckCircle className="h-3 w-3" />,
      className: 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800',
    },
    HYBRID: {
      label: 'Hybrid',
      icon: <Brain className="h-3 w-3" />,
      className: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800',
    },
  }

  const methodConfig = config[method] || {
    label: method,
    icon: null,
    className: 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700',
  }

  return (
    <div className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium', methodConfig.className)}>
      {methodConfig.icon}
      {methodConfig.label}
    </div>
  )
}

interface TransactionTableProps {
  transactions: BankTransaction[]
  isLoading: boolean
  selectedIds: Set<string>
  onToggleSelect: (id: string) => void
  onToggleSelectAll: () => void
  onUpdateStatus: (id: string, status: TransactionStatus) => void
  onApproveReview?: (transaction: BankTransaction) => void
  onRejectReview?: (id: string) => void
  canAddEdit: boolean
  isReviewQueue?: boolean
}

function TransactionTable({
  transactions,
  isLoading,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  onUpdateStatus,
  onApproveReview,
  onRejectReview,
  canAddEdit,
  isReviewQueue = false,
}: TransactionTableProps) {
  const colSpan = isReviewQueue ? 10 : 9
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {canAddEdit && !isReviewQueue && (
            <TableHead className="w-[40px]">
              <Checkbox
                checked={transactions.length > 0 && selectedIds.size === transactions.length}
                onCheckedChange={onToggleSelectAll}
              />
            </TableHead>
          )}
          <TableHead>Date</TableHead>
          <TableHead>Description</TableHead>
          <TableHead>Amount</TableHead>
          <TableHead>Account</TableHead>
          <TableHead>Method</TableHead>
          {!isReviewQueue && <TableHead>Status</TableHead>}
          <TableHead>Confidence</TableHead>
          {isReviewQueue && <TableHead>AI Reasoning</TableHead>}
          <TableHead className="w-[100px]">{isReviewQueue ? 'Actions' : ''}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {isLoading ? (
          <TableRow>
            <TableCell colSpan={colSpan} className="text-center py-8">Loading...</TableCell>
          </TableRow>
        ) : transactions.length === 0 ? (
          <TableRow>
            <TableCell colSpan={colSpan} className="text-center py-8">
              {isReviewQueue ? 'No transactions need review. Great job!' : 'No transactions found'}
            </TableCell>
          </TableRow>
        ) : (
          transactions.map((transaction) => (
            <TransactionRow
              key={transaction.transaction_id}
              transaction={transaction}
              isSelected={selectedIds.has(transaction.transaction_id)}
              onToggleSelect={() => onToggleSelect(transaction.transaction_id)}
              onUpdateStatus={onUpdateStatus}
              onApproveReview={onApproveReview}
              onRejectReview={onRejectReview}
              canAddEdit={canAddEdit}
              isReviewQueue={isReviewQueue}
            />
          ))
        )}
      </TableBody>
    </Table>
  )
}

interface TransactionRowProps {
  transaction: BankTransaction
  isSelected: boolean
  onToggleSelect: () => void
  onUpdateStatus: (id: string, status: TransactionStatus) => void
  onApproveReview?: (transaction: BankTransaction) => void
  onRejectReview?: (id: string) => void
  canAddEdit: boolean
  isReviewQueue?: boolean
}

function TransactionRow({
  transaction,
  isSelected,
  onToggleSelect,
  onUpdateStatus,
  onApproveReview,
  onRejectReview,
  canAddEdit,
  isReviewQueue = false,
}: TransactionRowProps) {
  const statusConfig = STATUS_CONFIG[transaction.status] || STATUS_CONFIG.PENDING
  const isCredit = transaction.direction === 'CREDIT'

  return (
    <TableRow className={cn(isSelected && 'bg-muted/50', isReviewQueue && 'bg-red-50/30 dark:bg-red-950/20')}>
      {canAddEdit && !isReviewQueue && (
        <TableCell>
          <Checkbox checked={isSelected} onCheckedChange={onToggleSelect} />
        </TableCell>
      )}
      <TableCell>{format(new Date(transaction.transaction_date), 'MMM d, yyyy')}</TableCell>
      <TableCell>
        <div className="max-w-[350px] space-y-1">
          {/* AI Parsed Description (if available) */}
          {(transaction.normalized_description || transaction.suggested_vendor) ? (
            <div className="flex items-start gap-1.5">
              <Sparkles className="h-3.5 w-3.5 text-purple-500 mt-0.5 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="font-medium text-purple-700 dark:text-purple-400 truncate"
                   title={transaction.normalized_description || transaction.suggested_vendor}>
                  {transaction.normalized_description || transaction.suggested_vendor}
                </p>
              </div>
            </div>
          ) : (
            /* Fallback to description if no AI parsing */
            <div className="flex items-start gap-1.5">
              {transaction.processing_method === 'RULE' && (
                <FileText className="h-3.5 w-3.5 text-blue-500 mt-0.5 flex-shrink-0" />
              )}
              <p className="font-medium truncate" title={transaction.description || transaction.raw_description || 'No description'}>
                {transaction.description || transaction.raw_description || <span className="text-muted-foreground italic">No description</span>}
              </p>
            </div>
          )}
          {/* Original Description - always show if available and different */}
          {(transaction.raw_description || transaction.description) && (
            <p className="text-xs text-muted-foreground truncate"
               title={transaction.raw_description || transaction.description}>
              <span className="text-muted-foreground/60 font-medium">Original:</span>{' '}
              {transaction.raw_description || transaction.description || '-'}
            </p>
          )}
          {/* Show suggested vendor if different from normalized_description */}
          {transaction.suggested_vendor &&
           transaction.normalized_description &&
           transaction.suggested_vendor !== transaction.normalized_description && (
            <p className="text-xs text-purple-600 dark:text-purple-400 flex items-center gap-1">
              <Sparkles className="h-3 w-3" />
              Vendor: {transaction.suggested_vendor}
            </p>
          )}
          {/* Show final vendor if set (user confirmed) */}
          {transaction.final_vendor && transaction.final_vendor !== transaction.suggested_vendor && (
            <p className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
              <CheckCircle className="h-3 w-3" />
              Confirmed: {transaction.final_vendor}
            </p>
          )}
        </div>
      </TableCell>
      <TableCell>
        <div className={cn('flex items-center gap-1 font-medium', isCredit ? 'text-green-600' : 'text-red-600')}>
          {isCredit ? <ArrowDownLeft className="h-4 w-4" /> : <ArrowUpRight className="h-4 w-4" />}
          {transaction.currency} {Math.abs(transaction.amount).toFixed(2)}
        </div>
      </TableCell>
      <TableCell>{transaction.bank_account_name || '-'}</TableCell>
      <TableCell>
        <ProcessingMethodBadge method={transaction.processing_method} />
      </TableCell>
      {!isReviewQueue && (
        <TableCell>
          <div className="flex flex-col gap-1">
            <Badge variant={statusConfig.variant}>{statusConfig.label}</Badge>
            {/* AI Category Suggestion Badge */}
            <CategorySuggestionBadge transaction={transaction} />
          </div>
        </TableCell>
      )}
      <TableCell>
        <ConfidenceBadge score={transaction.confidence_score} showIcon={isReviewQueue} />
      </TableCell>
      {isReviewQueue && (
        <TableCell>
          <div className="max-w-[250px]">
            {transaction.ai_reasoning ? (
              <p className="text-sm text-muted-foreground line-clamp-2" title={transaction.ai_reasoning}>
                {transaction.ai_reasoning}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground italic">No AI reasoning available</p>
            )}
          </div>
        </TableCell>
      )}
      <TableCell>
        {isReviewQueue && canAddEdit ? (
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="default"
              className="h-7 px-2"
              onClick={() => onApproveReview?.(transaction)}
            >
              <CheckCircle className="h-3.5 w-3.5 mr-1" />
              Approve
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-7 px-2"
              onClick={() => onRejectReview?.(transaction.transaction_id)}
            >
              <XCircle className="h-3.5 w-3.5" />
            </Button>
          </div>
        ) : canAddEdit ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onUpdateStatus(transaction.transaction_id, 'RECONCILED')}>
                <CheckCircle className="h-4 w-4 mr-2" />
                Mark Reconciled
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onUpdateStatus(transaction.transaction_id, 'IGNORED')}>
                <XCircle className="h-4 w-4 mr-2" />
                Ignore
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => onUpdateStatus(transaction.transaction_id, 'PENDING')}>
                Reset to Pending
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </TableCell>
    </TableRow>
  )
}

